import constants from "../constants";
import { CancelablePromise as Promise } from "./cancelablePromise";

export type TPromiseFunction = (resolve: Function, reject: Function) => void;

/**
 * Holds a FIFO Promise queue which only processes one promise at a time
 * and continues whether resolved or rejected. To be used to "lock" a
 * resource from being used by multiple asynchronous methods
 */
export default class PromiseQueue {

    /**
     * The current length of the queue
     */
    length: number;
    /**
     * Async Promise queue
     */
    private queue: Promise;

    /**
     * Populated error log method during construction - only used if enableLogger is set to true (for local testing since it is noisy)
     */
    private readonly logger?: ((message: string) => void) | null;

    /**
     * Populated error log method during construction
     */
    private readonly errorLog: (message: string) => void;

    /**
     * Populated error handler method during construction
     */
    private readonly errorHandler: Function;

    /**
     * The amount of milliseconds to wait for the queued promise to `resolve`, `reject`, or throw.
     * By default, this is 3 minutes.
     */
    private readonly timeout: number;

    constructor(logger: (message: string) => void, errorLog: (message: string) => void, errorHandler: Function, timeout = 180000, enableLogger = false) {
        this.reset();
        this.logger = enableLogger ? logger : null;
        this.errorLog = errorLog || constants.NOOP;
        this.errorHandler = errorHandler || constants.NOOP;
        this.timeout = timeout;
    }

    /**
     * Method builds up the queue with chained promises. Additionally, a separate promise is
     * returned, referencing the promise added to the queue. It won't be resolved immediately, but will
     * once the queue step is resolved.
     * @param callback The method that performs the action. It is REQUIRED to: `resolve`, `reject`, or throw.
     */
    push(callback: TPromiseFunction): Promise {
        let resolveQueueItem;
        let resolveFunc = arg => {
            this.length--;
            this.logger && this.logger(`QUEUE: Completed task. ${ this.length } task(s) in queue.`);
            resolveQueueItem(arg);
            return Promise.resolve();
        };
        this.length++;
        this.queue = this.queue.then(() => {
            this.logger && this.logger(`QUEUE: Processing task. ${ this.length } task(s) in queue.`);
            return this.getTask(callback).timeout(this.timeout);
        }).then(resolveFunc, err => {
            this.errorLog(`QUEUE: ${ err } (${ this.length } task(s) in queue)`);
            return resolveFunc(err);
        });

        return new Promise((resolve) => {
            resolveQueueItem = resolve;
        });
    }

    /**
     * Clears the promise queue by cancelling promises where available and resetting queue to empty
     */
    reset(){
        this.queue?.cancel && this.queue.cancel();
        this.queue = Promise.resolve(null);
        this.length = 0;
    }

    /**
     * Promisifies the callback with timeout
     * @param callback The method that performs the action. It is REQUIRED to: `resolve`, `reject`, or throw.
     */
    private getTask(callback: TPromiseFunction): Promise {
        return new Promise((resolve) => {
            try {
                callback(resolve, e => {
                    this.errorLog(`QUEUE: Promise rejected: ${ e }`);
                    resolve();
                });
            } catch (e) {
                let err: Error = e;
                this.errorLog(`${ err.name }: ${ err.message }. ${ err.stack }`);
                this.errorHandler(err);
                resolve();
            }
        });
    }
}